import { forwardRef } from "react";

import {
  Box,
  Pagination as MUIPagination,
  TablePaginationProps,
} from "@mui/material";
import {
  DataGridPro,
  GridPagination,
  gridFilteredTopLevelRowCountSelector,
  gridPageCountSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { i18n } from "translations";

import { DataGridProps, DataGridReference } from "./DataGrid.types";

function Pagination({
  page,
  className,
  onPageChange,
}: Pick<TablePaginationProps, "page" | "onPageChange" | "className">) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const visibleTopLevelRowCount = useGridSelector(
    apiRef,
    gridFilteredTopLevelRowCountSelector
  );
  const isClientMode = rootProps.paginationMode === "client";

  return (
    <MUIPagination
      showFirstButton
      showLastButton
      color="primary"
      shape="rounded"
      variant="outlined"
      className={className}
      count={
        isClientMode
          ? pageCount
          : Math.ceil(
              (rootProps.rowCount ?? visibleTopLevelRowCount) / pageSize
            )
      }
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(
          event as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>,
          newPage - 1
        );
      }}
    />
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomPagination(props: any) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}

const DataGrid = forwardRef<DataGridReference, DataGridProps>(
  ({
    containerProps,
    containerHeight,
    animation = "fade-in-bottom",
    columnHeaderHeight = 44,
    getRowHeight = () => "auto",
    disableRowSelectionOnClick = true,
    pageSizeOptions = [5, 10, 25, 50, 100, 300, 500],
    autoHeight: defaultAutoHeight,
    ...innerProps
  }) => {
    return (
      <Box
        className={animation}
        height={defaultAutoHeight ? undefined : containerHeight}
        sx={{
          "& .MuiDataGrid-overlayWrapper": {
            height: "100px",
          },
        }}
        {...containerProps}
      >
        <DataGridPro
          {...innerProps}
          columnHeaderHeight={columnHeaderHeight}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          pageSizeOptions={pageSizeOptions}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            ...innerProps.initialState,
          }}
          getRowHeight={getRowHeight}
          slots={{
            pagination: CustomPagination,
            ...innerProps.slots,
          }}
          localeText={{
            noRowsLabel: i18n.t("noData"),
            footerTotalRows: i18n.t("totalRows"),
          }}
          componentsProps={{
            pagination: {
              labelRowsPerPage: i18n.t("rowsPerPage"),
            },
          }}
        />
      </Box>
    );
  }
);

export default DataGrid;
